import type { FieldConfig, UserDefaultViewDto } from 'src/api/gen';
import {
  DEFAULT_VIEW_FALLBACK_ROUTE,
  HOME_PAGE_DEFAULT_VIEW_FEATURES,
} from 'src/constants/route';
import type { DefaultViewFeatureRoutePath } from 'src/types/route';

interface GetRoutePathParams {
  fieldConfig: FieldConfig | undefined;
  defaultView: UserDefaultViewDto | undefined;
}

/**
 * Determines the route path based on the user's default view and field configuration.
 *
 * Returns the route corresponding to the first matching feature column where:
 * - The user's default view choice is that feature.
 * - The fields configuration also has the feature enabled.
 *
 * Falls back to {@link DEFAULT_VIEW_FALLBACK_ROUTE} if no default view or field config is
 * provided, no matching feature is found, or the feature is disabled in the
 * field config.
 *
 * @param fieldConfig - The field configuration defining which features are enabled.
 * @param defaultView - The user's default view preferences.
 * @returns The resolved feature routes full path, or the fallback route.
 */
export const getRoutePathForDefaultView = ({
  fieldConfig,
  defaultView,
}: GetRoutePathParams): DefaultViewFeatureRoutePath => {
  if (!defaultView || !fieldConfig) return DEFAULT_VIEW_FALLBACK_ROUTE;

  const feature = HOME_PAGE_DEFAULT_VIEW_FEATURES.find(
    (f) => defaultView[f.defaultViewKey]
  );
  if (!feature) {
    return DEFAULT_VIEW_FALLBACK_ROUTE;
  }

  const isFeatureEnabled = fieldConfig[feature.fieldConfigKey];
  return isFeatureEnabled ? feature.route : DEFAULT_VIEW_FALLBACK_ROUTE;
};

/**
 * Checks if the provided value is an instance of `Error` with a `status`
 * property and, if a `statusCode` is provided, whether it matches the
 * error's `status`.
 *
 * @param error - The value to check.
 * @param statusCode - An optional status code to match against the error's
 * `status` property.
 * @returns `true` if the value is an instance of `Error` with a `status`
 * property and if the `statusCode` matches (if provided); otherwise, `false`.
 */
export function isErrorWithStatusCode(
  error: unknown,
  statusCode?: number
): error is Error & { status: number } {
  const hasErrorStatus =
    error instanceof Error &&
    'status' in error &&
    typeof error.status === 'number';
  return statusCode
    ? hasErrorStatus && error.status === statusCode
    : hasErrorStatus;
}

/**
 * Converts a string to kebab-case.
 * Handles camelCase, PascalCase, spaces, underscores, and uppercase text.
 *
 * @param input - The string to convert.
 * @returns The kebab-case version of the string.
 */
export function toKebabCase(input: string): string {
  if (!input) return '';
  return input
    .replaceAll(/(?<lower>[a-z0-9])(?<upper>[A-Z])/g, '$<lower>-$<upper>')
    .replaceAll(
      /(?<upper>[A-Z])(?<upperNext>[A-Z])(?<lower>[a-z])/g,
      '$<upper>-$<upperNext>$<lower>'
    )
    .replaceAll(/[-_\s]+/g, '-')
    .toLowerCase()
    .replaceAll(/^-+|-+$/g, '');
}
